import { cn } from '@utazzo/ui';
import React from 'react';

/**
 * Shared look for every month-comparison table in the Marketing report.
 *
 * All of them read the same way, deliberately: months across the top (newest
 * first, matching the source spreadsheet), the thing being measured down the
 * side, banded into groups. Metrics as rows rather than columns is what keeps
 * these readable - there are far more metrics than months, so this way a report
 * fits the screen instead of scrolling sideways past two dozen columns.
 *
 * The classes live here rather than in each table so the four cannot drift
 * apart: change the banding or the headline treatment once and every month
 * table follows.
 */

export interface MonthColumn {
  month: string;
  label: string;
}

export const MONTH_TABLE = 'w-full border-separate border-spacing-0 text-[15px]';

/**
 * Excel gridlines for the report's tables. Goes WITH `border-separate
 * border-spacing-0`, never with `border-collapse`.
 *
 * A collapsing table cannot hold a sticky header: Chrome paints the collapsed
 * row borders and the body rows over the pinned `<th>`, so rows slide across
 * the header instead of under it. Separated borders are the fix, and then each
 * cell has to draw only its bottom and right edge - a full box per cell would
 * double every interior line, since nothing collapses them. The first column
 * adds the left edge and the header row the top one.
 *
 * Put on the `<table>` rather than on each cell: these tables render cells in a
 * dozen places (group rows, nested rows, totals, expanders) and a per-cell class
 * is one `cn(...)` away from a row with no lines. The client reads this report
 * as a grid, so a missing line is a real defect, not a cosmetic one.
 */
export const GRID_TABLE = [
  'border-separate border-spacing-0',
  '[&_td]:border-border [&_th]:border-border',
  '[&_td]:border-r [&_td]:border-b [&_th]:border-r [&_th]:border-b',
  '[&_tr>*:first-child]:border-l',
  '[&_thead_tr>*]:border-t',
].join(' ');

/**
 * Header band shared by the report's tables. Muted rather than tinted: the
 * gridlines already separate one table from the next, and a coloured band on
 * six stacked tables reads as decoration rather than as structure.
 */
export const REPORT_HEAD_CELL =
  'border-border bg-muted text-muted-foreground sticky top-0 z-20 px-3 py-2 text-xs font-semibold tracking-wide uppercase';

/** "Grand Total" - the bold summary row under a table. */
export const GRAND_TOTAL_ROW = 'bg-muted font-semibold';

/**
 * While true, every collapsible group in the report renders OPEN.
 *
 * The Excel export reads the rendered tables, so whatever is folded on screen
 * is simply absent from the file - and the sheet now opens folded, which would
 * hand over a workbook of band headings and no detail. Turning this on for the
 * moment of the export is what puts the whole report in the file, whatever the
 * operator happened to have open.
 *
 * A context, not a prop: the groups sit several components below the button.
 */
export const ForceExpandContext = React.createContext(false);

/** Read inside a collapsible table: is this group open right now? */
export const useGroupOpen = (): ((open: boolean) => boolean) => {
  const forced = React.useContext(ForceExpandContext);
  return (open: boolean) => forced || open;
};

/**
 * Figure colours, shared by every table in the report.
 *
 * Text only, never a fill: the sheet stays plain and the colour reads as a
 * property of the number rather than as highlighting. Kept deliberately light,
 * a single step off the body colour, so a column of them does not shout.
 */
export const NUMBER_TONE = {
  /** An outcome in the business's favour - won, converted, contacted. */
  good: 'text-emerald-700 dark:text-emerald-400',
  /** An outcome against it - lost, unreached, a negative figure. */
  bad: 'text-rose-600 dark:text-rose-400',
  /** Nothing happened here. Faded so the non-zero figures carry the eye. */
  zero: 'text-muted-foreground/60',
} as const;

/**
 * The colour for one figure, from what its COLUMN means - not from a threshold.
 *
 * A "good at 70%, bad at 50%" rule would be this file inventing targets the
 * business has not set. What is safe to say is that Lost counts against and Won
 * counts for, and that a zero is worth fading whichever column it lands in.
 */
export const numberTone = (value: number | null, sense?: 'good' | 'bad'): string => {
  if (value === null) return NUMBER_TONE.zero;
  if (value === 0) return NUMBER_TONE.zero;
  return sense ? NUMBER_TONE[sense] : '';
};

/** Sign-driven colour, for a figure that can go either way (profit, margin). */
export const signedTone = (value: number | null): string => {
  if (value === null || value === 0) return NUMBER_TONE.zero;
  return value < 0 ? NUMBER_TONE.bad : NUMBER_TONE.good;
};

/**
 * Sticky header: the row label column pins left, the months pin top. Sticky
 * lives on the CELLS, not the `<tr>` - `position: sticky` on a table row is
 * unreliable across browsers.
 */
export const MonthHeaderRow = ({
  label,
  months,
  trailing,
}: {
  /** What the first column holds - "Metric", "Channel", "Status"… */
  label: string;
  months: MonthColumn[];
  /** Optional extra right-hand column, e.g. a total across the months. */
  trailing?: string;
}): React.JSX.Element => (
  <tr>
    <th className="bg-muted text-foreground border-border sticky top-0 left-0 z-40 border-t border-r border-b border-l px-3 py-2 text-left text-xs font-semibold tracking-wide uppercase">
      {label}
    </th>
    {months.map((month, index) => (
      <th
        key={month.month}
        className={cn(
          'bg-muted border-border sticky top-0 z-20 border-t border-r border-b px-3 py-2 text-right text-xs font-semibold whitespace-nowrap',
          // Newest month first - the one the KPI tiles summarise, so it reads
          // at full strength while the comparison months sit back.
          index === 0 ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {month.label}
      </th>
    ))}
    {trailing && (
      <th className="bg-muted text-foreground border-border sticky top-0 z-20 border-t border-r border-b px-3 py-2 text-right text-xs font-semibold whitespace-nowrap">
        {trailing}
      </th>
    )}
  </tr>
);

/**
 * Section band across the table - "Spend", a channel name, a status group. The
 * label sits in its own sticky cell so it stays readable when the months scroll
 * sideways; the rest of the band is an empty spanning cell.
 */
export const GroupBandRow = ({
  span,
  children,
  tone = 'group',
}: {
  /** Number of value columns the band has to cover. */
  span: number;
  children: React.ReactNode;
  /** `group` for a metric group, `entity` for a named row-group heading. */
  tone?: 'group' | 'entity';
}): React.JSX.Element => {
  const band = tone === 'entity' ? 'bg-muted/70' : 'bg-muted/60';
  return (
    <tr>
      <th
        scope="rowgroup"
        className={cn(
          'border-border sticky left-0 z-10 border-r border-b border-l px-3 py-1.5 text-left',
          band,
          tone === 'entity'
            ? 'text-foreground text-sm font-semibold'
            : 'text-muted-foreground text-[11px] font-semibold tracking-wide uppercase',
        )}
      >
        {children}
      </th>
      <td colSpan={span} className={cn('border-border border-r border-b', band)} />
    </tr>
  );
};

/** Row wrapper - carries the hover group the sticky label cell keys off. */
export const MONTH_ROW = 'hover:bg-muted/40 group';

/**
 * The sticky row-label cell. `headline` marks a figure the eye should land on
 * first; it gets an accent rail rather than a background tint so the highlight
 * doesn't fight the green/red tinting on the values themselves.
 */
export const labelCell = (options?: {
  headline?: boolean;
  /** 0 = top level, 1 = nested, 2 = nested twice. */
  depth?: 0 | 1 | 2;
  muted?: boolean;
}): string => {
  const indent = ['pl-3', 'pl-7', 'pl-11'][options?.depth ?? 0]!;
  return cn(
    'bg-card group-hover:bg-muted border-border sticky left-0 z-10 border-r border-b border-l py-1 pr-3 text-left align-middle font-normal whitespace-nowrap transition-colors',
    indent,
    // Full-strength text, as a spreadsheet has it - a row label is the name of
    // the figure beside it, not secondary matter to be greyed back.
    options?.headline
      ? 'border-l-primary text-foreground border-l-2 font-semibold'
      : options?.muted
        ? 'text-muted-foreground'
        : 'text-foreground',
  );
};

/** A value cell under one month. `tone` carries the green/red sign tint. */
export const valueCell = (options?: {
  headline?: boolean;
  muted?: boolean;
  tone?: string;
}): string =>
  cn(
    'border-border border-r border-b px-3 py-1 text-right whitespace-nowrap tabular-nums',
    options?.headline ? 'text-foreground font-semibold' : 'text-foreground/90',
    options?.muted && 'text-muted-foreground',
    options?.tone,
  );

/** Bold summary row pinned under the body - grand totals and the like. */
export const TOTAL_LABEL_CELL =
  'bg-muted text-foreground border-border sticky left-0 z-10 border-t border-r border-b border-l px-3 py-2 text-left font-semibold whitespace-nowrap';
export const TOTAL_VALUE_CELL =
  'bg-muted text-foreground border-border border-t border-r border-b px-3 py-2 text-right font-semibold whitespace-nowrap tabular-nums';

/** Small grey derivation note under a row label ("Profit minus total spend"). */
export const RowHint = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <span
    data-export-skip
    className="text-muted-foreground/70 block text-[11px] font-normal whitespace-nowrap"
  >
    {children}
  </span>
);
